// frontend/src/pages/ParentDashboard.js
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import "../styles/ParentDashboard.css";
import { formatRaceDate } from "../utils/dateUtils";
import DeleteRacerConfirmModal from "../components/DeleteRacerConfirmModal"; // ✅

/** ---------------------------
 * Division rules (2026)
 * ---------------------------
 * - Ages 8–9: MUST be Lil Stingers Division
 * - Age 7: may be Snack Pack OR Lil Stingers Division
 * - Under 7: cannot be Lil Stingers Division
 *
 * NOTE: We use ONE canonical string everywhere:
 * ✅ "Lil Stingers Division"
 */
const DIVISIONS = {
    THREE: "3 Year Old Division",
    FOUR: "4 Year Old Division",
    FIVE: "5 Year Old Division",
    SNACK: "Snack Pack Division",
    STINGERS: "Lil Stingers Division",
};

const DIVISION_DESCRIPTIONS = {
    [DIVISIONS.SNACK]: "Electric Battery",
    [DIVISIONS.STINGERS]: "50 CC Engine",
};

const getDivisionFromAge = (ageRaw) => {
    const age = Number(ageRaw);

    if (age === 2 || age === 3) return DIVISIONS.THREE;
    if (age === 4) return DIVISIONS.FOUR;
    if (age === 5) return DIVISIONS.FIVE;
    if (age === 6) return DIVISIONS.SNACK;

    // default for age 7 if parent doesn’t choose
    if (age === 7) return DIVISIONS.SNACK;

    // ages 8–9 must be Lil Stingers
    if (age === 8 || age === 9) return DIVISIONS.STINGERS;

    return "N/A";
};

const getDivisionForSave = (ageRaw, selectedDivisionRaw) => {
    const age = Number(ageRaw);
    const selected = (selectedDivisionRaw || "").trim();

    // Age 7: allow Snack Pack OR Lil Stingers
    if (age === 7) {
        if (selected === DIVISIONS.STINGERS) return DIVISIONS.STINGERS;
        return DIVISIONS.SNACK; // default
    }

    // Age 8–9: force Lil Stingers
    if (age === 8 || age === 9) return DIVISIONS.STINGERS;

    // Under 7: force computed (and prevents stingers)
    return getDivisionFromAge(age);
};

const divisionLabelWithDesc = (division) => {
    const desc = DIVISION_DESCRIPTIONS[division];
    return desc ? `${division} — ${desc}` : division;
};

function ParentDashboard() {
    const [parent, setParent] = useState(null);
    const [racers, setRacers] = useState([]);
    const [races, setRaces] = useState([]);

    const [newRacer, setNewRacer] = useState({
        firstName: "",
        lastName: "",
        nickname: "",
        age: "",
        carNumber: "",
        division: "", // ✅ only used when age === 7
    });

    const [editingRacer, setEditingRacer] = useState(null);

    const [statusMessage, setStatusMessage] = useState("");
    const [loading, setLoading] = useState(true);

    const [registrations, setRegistrations] = useState({});
    // key: `${racerId}|${raceId}` -> { id, racerId, raceId }

    // ✅ Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [racerToDelete, setRacerToDelete] = useState(null);

    // 👥 Co-parent / Guardian invite state
    const [coParentEmail, setCoParentEmail] = useState("");
    const [inviteStatus, setInviteStatus] = useState("");
    const [inviteLoading, setInviteLoading] = useState(false);

    const navigate = useNavigate();

    // 🔄 Load parent, racers, races, and registrations
    useEffect(() => {
        const token = localStorage.getItem("token");

        // If not logged in, go back to login
        if (!token) {
            navigate("/login");
            return;
        }

        const loadDashboard = async () => {
            try {
                setLoading(true);
                setStatusMessage("");

                // 1) Who is the logged-in parent?
                const parentRes = await apiClient.get("/auth/me");
                setParent(parentRes.data);

                // 2) Racers for this parent
                const racersRes = await apiClient.get("/racers");
                setRacers(racersRes.data || []);

                // 3) Races (only ones that require registration)
                const racesRes = await apiClient.get("/races");

                const mappedRaces = (racesRes.data || []).map((race) => ({
                    id: race.id,
                    name: race.raceName,
                    date: race.raceDate,
                    location: race.location,
                    description: race.description,
                    requiresRegistration: race.requiresRegistration ?? true,
                }));

                // ✅ Remove info-only events from parent dashboard
                const registrationOnlyRaces = mappedRaces.filter((r) => r.requiresRegistration === true);
                setRaces(registrationOnlyRaces);

                // 4) Existing registrations
                try {
                    const regsRes = await apiClient.get("/registrations/mine");
                    const regMap = {};
                    (regsRes.data || []).forEach((reg) => {
                        const key = `${reg.racerId}|${reg.raceId}`;
                        regMap[key] = reg;
                    });
                    setRegistrations(regMap);
                } catch (regErr) {
                    console.warn("No /registrations/mine endpoint yet or it failed.", regErr);
                }
            } catch (err) {
                console.error("Error loading dashboard:", err);
                const status = err.response?.status;

                if (status === 401 || status === 403) {
                    sessionStorage.setItem("authMessage", "Your session expired. Please log in again.");
                    localStorage.removeItem("token");
                    localStorage.removeItem("firstName");
                    localStorage.removeItem("role");
                    navigate("/login");
                } else {
                    setStatusMessage("❌ Could not load dashboard. Please try again.");
                }
            } finally {
                setLoading(false);
            }
        };

        void loadDashboard();
    }, [navigate]);

    // ✅ UX: Next upcoming race card
    const nextRace = useMemo(() => {
        if (!races || races.length === 0) return null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const upcoming = races
            .filter((r) => r?.date && new Date(r.date) >= today)
            .sort((a, b) => new Date(a.date) - new Date(b.date));

        return upcoming[0] || null;
    }, [races]);

    // ✅ UX: Summary "You have X racers registered for Y events"
    const registrationSummary = useMemo(() => {
        const regValues = Object.values(registrations || {});
        const uniqueRacerIds = new Set();
        const uniqueRaceIds = new Set();

        regValues.forEach((reg) => {
            if (reg?.racerId) uniqueRacerIds.add(reg.racerId);
            if (reg?.raceId) uniqueRaceIds.add(reg.raceId);
        });

        return {
            registeredRacersCount: uniqueRacerIds.size,
            totalEventsCount: uniqueRaceIds.size,
        };
    }, [registrations]);

    // === Logout ===
    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("firstName");
        localStorage.removeItem("role");
        navigate("/login");
    };

    // === Racer CRUD ===
    const handleAddRacer = (e) => {
        e.preventDefault();

        const ageNum = Number(newRacer.age);

        const payload = {
            firstName: (newRacer.firstName || "").trim(),
            lastName: (newRacer.lastName || "").trim(),
            nickname: (newRacer.nickname || "").trim(),
            age: ageNum,
            carNumber: (newRacer.carNumber || "").trim(),
            division: getDivisionForSave(ageNum, newRacer.division),
        };

        // UI guardrails (backend should enforce too)
        if (ageNum < 7 && payload.division === DIVISIONS.STINGERS) {
            setStatusMessage("❌ Under age 7 cannot be in Lil Stingers.");
            setTimeout(() => setStatusMessage(""), 3500);
            return;
        }
        if ((ageNum === 8 || ageNum === 9) && payload.division !== DIVISIONS.STINGERS) {
            setStatusMessage("❌ Age 8–9 must be in Lil Stingers.");
            setTimeout(() => setStatusMessage(""), 3500);
            return;
        }
        if (ageNum === 7 && ![DIVISIONS.SNACK, DIVISIONS.STINGERS].includes(payload.division)) {
            setStatusMessage("❌ Age 7 must be Snack Pack or Lil Stingers.");
            setTimeout(() => setStatusMessage(""), 3500);
            return;
        }

        apiClient
            .post("/racers", payload)
            .then((res) => {
                setRacers([...racers, res.data]);
                setNewRacer({
                    firstName: "",
                    lastName: "",
                    nickname: "",
                    age: "",
                    carNumber: "",
                    division: "",
                });
                setStatusMessage("✅ Racer added successfully!");
                setTimeout(() => setStatusMessage(""), 2500);
            })
            .catch((err) => {
                console.error("Error adding racer:", err);
                const msg = err?.response?.data?.message || err?.response?.data || "❌ Error adding racer.";
                setStatusMessage(typeof msg === "string" ? msg : "❌ Error adding racer.");
                setTimeout(() => setStatusMessage(""), 3500);
            });
    };

    const startEdit = (racer) => {
        setEditingRacer({
            ...racer,
            nickname: racer?.nickname || "",
            division: racer?.division || "",
        });
    };

    const handleSaveEdit = () => {
        if (!editingRacer?.id) return;

        const ageNum = Number(editingRacer.age);

        const payload = {
            ...editingRacer,
            firstName: (editingRacer.firstName || "").trim(),
            lastName: (editingRacer.lastName || "").trim(),
            nickname: (editingRacer.nickname || "").trim(),
            age: ageNum,
            carNumber: (editingRacer.carNumber || "").trim(),
            division: getDivisionForSave(ageNum, editingRacer.division),
        };

        // UI guardrails (backend should enforce too)
        if (ageNum < 7 && payload.division === DIVISIONS.STINGERS) {
            setStatusMessage("❌ Under age 7 cannot be in Lil Stingers.");
            setTimeout(() => setStatusMessage(""), 3500);
            return;
        }
        if ((ageNum === 8 || ageNum === 9) && payload.division !== DIVISIONS.STINGERS) {
            setStatusMessage("❌ Age 8–9 must be in Lil Stingers.");
            setTimeout(() => setStatusMessage(""), 3500);
            return;
        }
        if (ageNum === 7 && ![DIVISIONS.SNACK, DIVISIONS.STINGERS].includes(payload.division)) {
            setStatusMessage("❌ Age 7 must be Snack Pack or Lil Stingers.");
            setTimeout(() => setStatusMessage(""), 3500);
            return;
        }

        apiClient
            .put(`/racers/${editingRacer.id}`, payload)
            .then((res) => {
                const updated = racers.map((r) => (r.id === res.data.id ? res.data : r));
                setRacers(updated);
                setEditingRacer(null);
                setStatusMessage("✅ Racer updated!");
                setTimeout(() => setStatusMessage(""), 2000);
            })
            .catch((err) => {
                console.error("Error saving racer:", err);
                const msg = err?.response?.data?.message || err?.response?.data || "❌ Error updating racer.";
                setStatusMessage(typeof msg === "string" ? msg : "❌ Error updating racer.");
                setTimeout(() => setStatusMessage(""), 3500);
            });
    };

    // ✅ open delete modal instead of deleting immediately
    const openDeleteRacer = (racer) => {
        setRacerToDelete(racer);
        setDeleteModalOpen(true);
    };

    // ✅ confirmed delete
    const confirmDeleteRacer = async (racerId) => {
        try {
            await apiClient.delete(`/racers/${racerId}`);

            // remove racer
            setRacers((prev) => prev.filter((r) => r.id !== racerId));

            // remove any registrations associated with this racer
            setRegistrations((prev) => {
                const copy = { ...prev };
                Object.keys(copy).forEach((key) => {
                    if (key.startsWith(`${racerId}|`)) delete copy[key];
                });
                return copy;
            });

            setStatusMessage("🗑️ Racer deleted.");
        } catch (err) {
            console.error("Error deleting racer:", err);
            setStatusMessage("❌ Error removing racer.");
        } finally {
            setDeleteModalOpen(false);
            setRacerToDelete(null);
            setTimeout(() => setStatusMessage(""), 2000);
        }
    };

    // === Race Registration ===
    const handleRaceRegistration = async (racerId, raceId, checked) => {
        const key = `${racerId}|${raceId}`;

        try {
            if (checked) {
                const res = await apiClient.post("/registrations", { racerId, raceId });
                setRegistrations((prev) => ({
                    ...prev,
                    [key]: res.data,
                }));
                setStatusMessage("🏁 Racer registered for race!");
            } else {
                const existing = registrations[key];
                if (!existing) return;

                await apiClient.delete(`/registrations/${existing.id}`);

                setRegistrations((prev) => {
                    const copy = { ...prev };
                    delete copy[key];
                    return copy;
                });
                setStatusMessage("❌ Racer unregistered from race.");
            }
        } catch (err) {
            console.error("Error updating race registration:", err);
            setStatusMessage("❌ Error updating registration.");
        } finally {
            setTimeout(() => setStatusMessage(""), 2000);
        }
    };

    // === Co-parent / Guardian invite ===
    const handleInviteSubmit = async (e) => {
        e.preventDefault();
        setInviteStatus("");
        setInviteLoading(true);

        try {
            await apiClient.post("/parents/invite", { email: coParentEmail });
            setInviteStatus(
                "✅ Invite sent! If they already have an account, they'll see your racers. If not, they can sign up with this email."
            );
            setCoParentEmail("");
        } catch (err) {
            console.error("Error sending co-parent invite:", err);
            const msg = err.response?.data?.message || "Sorry, we couldn't send that invite. Please try again.";
            setInviteStatus("❌ " + msg);
        } finally {
            setInviteLoading(false);
        }
    };

    // === Rendering ===
    if (loading || !parent) {
        return <p>Loading dashboard...</p>;
    }

    const racerToDeleteName = racerToDelete
        ? `${racerToDelete.firstName || ""} ${racerToDelete.lastName || ""}`.trim()
        : "this racer";

    return (
        <div className="dashboard-container">
            {/* ✅ Delete confirmation modal */}
            <DeleteRacerConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDeleteRacer}
                racerId={racerToDelete?.id}
                racerName={racerToDeleteName}
            />

            {/* Header with welcome + co-parent invite + logout */}
            <div className="dashboard-header">
                <div className="welcome-section">
                    <h1>
                        Welcome back, <span>{parent.firstName}</span>! 🏁
                    </h1>
                    <p className="tagline">Ready to build champions, one lap at a time.</p>
                </div>

                <div className="header-right">
                    <div className="coparent-inline">
                        <h3>👥 Invite a Co-Guardian</h3>
                        <form className="coparent-form-inline" onSubmit={handleInviteSubmit}>
                            <input
                                type="email"
                                placeholder="Co-Guardian email"
                                value={coParentEmail}
                                onChange={(e) => setCoParentEmail(e.target.value)}
                                required
                            />
                            <button type="submit" disabled={inviteLoading}>
                                {inviteLoading ? "Sending..." : "Send Invite"}
                            </button>
                        </form>
                        <p>Co-Guardian must be registered to receive invite.</p>
                        {inviteStatus && <p className="coparent-status">{inviteStatus}</p>}
                    </div>

                    <button className="logout-button" onClick={handleLogout} title="Log out">
                        Logout
                    </button>
                </div>
            </div>

            {/* ✅ Next Race + Summary */}
            <div className="dashboard-top-row">
                <div className="next-race-card">
                    <h2 className="next-race-title">🏁 Next Upcoming Race</h2>

                    {nextRace ? (
                        <>
                            <div className="next-race-name">{nextRace.name}</div>
                            <div className="next-race-meta">
                                <span>📅 {formatRaceDate(nextRace.date)}</span>
                                {nextRace.location ? <span>📍 {nextRace.location}</span> : null}
                            </div>
                            {nextRace.description ? <p className="next-race-desc">{nextRace.description}</p> : null}
                        </>
                    ) : (
                        <p className="next-race-empty">No upcoming races scheduled yet.</p>
                    )}
                </div>

                <div className="registration-summary-card">
                    <h2 className="summary-title">📌 Quick Summary</h2>
                    <p className="summary-text">
                        You have <b>{registrationSummary.registeredRacersCount}</b> racer(s) registered for{" "}
                        <b>{registrationSummary.totalEventsCount}</b> total event(s).
                    </p>
                </div>
            </div>

            {/* === Racer Section === */}
            <section className="racer-section">
                <h2>Your Racers</h2>

                <ul className="racer-list">
                    {racers.map((racer) => {
                        // ✅ normalize "Lil Stingers" -> "Lil Stingers Division" if older data exists
                        const rawDiv = (racer?.division || "").trim();
                        const normalizedDiv =
                            rawDiv === "Lil Stingers" ? DIVISIONS.STINGERS : rawDiv;

                        const shownDivision = normalizedDiv || getDivisionFromAge(racer.age);
                        const shownDivisionWithDesc = divisionLabelWithDesc(shownDivision);

                        return (
                            <li key={racer.id} className="racer-item">
                                {editingRacer && editingRacer.id === racer.id ? (
                                    <div className="edit-form">
                                        <input
                                            type="text"
                                            value={editingRacer.firstName || ""}
                                            onChange={(e) => setEditingRacer({ ...editingRacer, firstName: e.target.value })}
                                            placeholder="First Name"
                                        />

                                        <input
                                            type="text"
                                            value={editingRacer.lastName || ""}
                                            onChange={(e) => setEditingRacer({ ...editingRacer, lastName: e.target.value })}
                                            placeholder="Last Name"
                                        />

                                        <input
                                            type="text"
                                            value={editingRacer.nickname || ""}
                                            onChange={(e) => setEditingRacer({ ...editingRacer, nickname: e.target.value })}
                                            placeholder="Nickname (optional)"
                                        />

                                        <input
                                            type="number"
                                            value={editingRacer.age || ""}
                                            onChange={(e) => setEditingRacer({ ...editingRacer, age: e.target.value })}
                                            placeholder="Age"
                                            min="2"
                                            max="9"
                                        />

                                        {/* ✅ Division select only if age 7 (with descriptions) */}
                                        {Number(editingRacer.age) === 7 ? (
                                            <>
                                                <select
                                                    value={getDivisionForSave(editingRacer.age, editingRacer.division)}
                                                    onChange={(e) => setEditingRacer({ ...editingRacer, division: e.target.value })}
                                                >
                                                    <option value={DIVISIONS.SNACK}>
                                                        {divisionLabelWithDesc(DIVISIONS.SNACK)}
                                                    </option>
                                                    <option value={DIVISIONS.STINGERS}>
                                                        {divisionLabelWithDesc(DIVISIONS.STINGERS)}
                                                    </option>
                                                </select>
                                                <p className="age-note" style={{ marginTop: 6 }}>
                                                    Snack Pack = <b>Electric Battery</b> • Lil Stingers = <b>50 CC Engine</b>
                                                </p>
                                            </>
                                        ) : null}

                                        <input
                                            type="text"
                                            value={editingRacer.carNumber || ""}
                                            onChange={(e) => setEditingRacer({ ...editingRacer, carNumber: e.target.value })}
                                            placeholder="Car Number"
                                        />

                                        <div className="edit-buttons">
                                            <button className="save-btn" onClick={handleSaveEdit}>
                                                Save
                                            </button>
                                            <button className="cancel-btn" onClick={() => setEditingRacer(null)}>
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="racer-info">
                                            <strong>
                                                {racer.firstName} {racer.lastName}
                                                {racer.nickname ? ` (${racer.nickname})` : ""}
                                            </strong>{" "}
                                            — #{racer.carNumber} ({racer.age} yrs) — <b>{shownDivisionWithDesc}</b>
                                        </div>
                                        <div className="racer-actions">
                                            <button className="edit-btn" onClick={() => startEdit(racer)}>
                                                Edit
                                            </button>
                                            <button className="remove-btn" onClick={() => openDeleteRacer(racer)}>
                                                Remove
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        );
                    })}
                </ul>

                {/* === Add Racer Form === */}
                <form onSubmit={handleAddRacer} className="racer-form">
                    <h3>Add a New Racer</h3>
                    <p className="age-note">* Age is determined as of January 1 of the race season.</p>

                    <input
                        type="text"
                        placeholder="First Name"
                        value={newRacer.firstName}
                        onChange={(e) => setNewRacer({ ...newRacer, firstName: e.target.value })}
                        required
                    />

                    <input
                        type="text"
                        placeholder="Last Name"
                        value={newRacer.lastName}
                        onChange={(e) => setNewRacer({ ...newRacer, lastName: e.target.value })}
                        required
                    />

                    <input
                        type="text"
                        placeholder="Nickname (optional)"
                        value={newRacer.nickname}
                        onChange={(e) => setNewRacer({ ...newRacer, nickname: e.target.value })}
                    />

                    <input
                        type="number"
                        placeholder="Age"
                        value={newRacer.age}
                        onChange={(e) => setNewRacer({ ...newRacer, age: e.target.value })}
                        required
                        min="2"
                        max="9"
                    />

                    {/* ✅ Only show division choice for age 7 (with descriptions) */}
                    {Number(newRacer.age) === 7 ? (
                        <>
                            <label style={{ marginTop: 8, fontWeight: 600 }}>Division (Age 7 only)</label>
                            <select
                                value={getDivisionForSave(newRacer.age, newRacer.division)}
                                onChange={(e) => setNewRacer({ ...newRacer, division: e.target.value })}
                                required
                            >
                                <option value={DIVISIONS.SNACK}>{divisionLabelWithDesc(DIVISIONS.SNACK)}</option>
                                <option value={DIVISIONS.STINGERS}>{divisionLabelWithDesc(DIVISIONS.STINGERS)}</option>
                            </select>

                            <p className="age-note" style={{ marginTop: 6 }}>
                                Snack Pack = <b>Electric Battery</b> • Lil Stingers = <b>50 CC Engine</b>
                            </p>
                        </>
                    ) : null}

                    <input
                        type="text"
                        placeholder="Car Number"
                        value={newRacer.carNumber}
                        onChange={(e) => setNewRacer({ ...newRacer, carNumber: e.target.value })}
                        required
                    />

                    <button type="submit">Add Racer</button>
                </form>
            </section>

            {/* === Race Registration Section === */}
            <section className="race-registration">
                <h2>Race Registrations</h2>
                {racers.length === 0 ? (
                    <p>Add a racer above to start registering for events.</p>
                ) : races.length === 0 ? (
                    <p>No races available yet. Check back soon!</p>
                ) : (
                    racers.map((racer) => (
                        <div key={racer.id} className="racer-card">
                            <h3>
                                {racer.firstName} {racer.lastName}
                                {racer.nickname ? ` (${racer.nickname})` : ""} — #{racer.carNumber}
                            </h3>

                            <div className="race-list">
                                {races.map((race) => {
                                    const key = `${racer.id}|${race.id}`;
                                    const isRegistered = !!registrations[key];

                                    return (
                                        <div key={race.id} className={`race-item ${isRegistered ? "registered" : ""}`}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isRegistered}
                                                    onChange={(e) => handleRaceRegistration(racer.id, race.id, e.target.checked)}
                                                />
                                                {`${race.name} — ${formatRaceDate(race.date)}`}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}
            </section>

            {statusMessage && <div className="status-message">{statusMessage}</div>}
        </div>
    );
}

export default ParentDashboard;